import type {
  DrawingEventType,
  EntityId,
  IChartingLibraryWidget,
  IChartWidgetApi,
} from 'public/static/charting_library/charting_library';
import { useEffect, useState } from 'react';

type Options = {
  enabled?: boolean;
  price?: number | null;
  lineColor?: string;
  lineStyle?: number;
  text?: string;
  fontSize?: number;
  textColor?: string;
  horzLabelsAlign?: string;
  draggable?: boolean;
  /**
   * Called with the new price when the user drags the line. Must be referentially
   * stable (e.g. `useCallback`) to avoid resubscribing on every render.
   */
  onPriceChange?: (price: number) => void;
};

/**
 * Run a callback against the widget's chart API. The parent nullifies the widget
 * before destroying it, so a non-null widget is normally safe — the one
 * exception is the awaited `createShape` below, whose call can span a teardown.
 * Routing chart access through here keeps that single tolerated failure in one
 * place instead of scattering `try/catch` across every call site.
 */
const withChart = <T>(
  widget: IChartingLibraryWidget,
  fn: (chart: IChartWidgetApi) => T,
): T | undefined => {
  try {
    return fn(widget.chart());
  } catch {
    return undefined;
  }
};

export const useHorizontalLine = (
  widget: IChartingLibraryWidget | null | undefined,
  options: Options,
) => {
  const [line, setLine] = useState<EntityId | null>(null);
  const {
    price,
    enabled = true,
    lineColor = '#b1adb4',
    lineStyle = 0,
    text,
    fontSize = 12,
    textColor = '#b1adb4',
    horzLabelsAlign,
    draggable = false,
    onPriceChange,
  } = options;

  // The widget is recreated on token/theme/price-view changes, which orphans
  // any shape we created on the previous instance. Drop the stale id so the
  // line is recreated on the new widget.
  useEffect(() => {
    setLine(null);
  }, [widget]);

  useEffect(() => {
    if (!widget) return;

    if (enabled && price != null && Number.isFinite(price) && !line) {
      let cancelled = false;
      (async () => {
        const id = await withChart(widget, (chart) =>
          chart.createShape(
            { time: Math.floor(Date.now() / 1000), price },
            {
              shape: 'horizontal_line',
              lock: !draggable,
              disableSelection: !draggable,
              disableSave: true,
              disableUndo: true,
              zOrder: 'top',
              overrides: {
                linecolor: lineColor,
                linestyle: lineStyle,
                text: text ?? undefined,
                fontsize: fontSize,
                textcolor: textColor,
                showLabel: !!text,
                horzLabelsAlign: horzLabelsAlign ?? undefined,
              },
            },
          ),
        )?.catch(() => undefined);
        if (id == null) return;

        // The effect was torn down (rapid price change or widget swap) before
        // the shape resolved; it's already on the chart, so remove it rather
        // than orphaning it.
        if (cancelled) {
          withChart(widget, (chart) => chart.removeEntity(id));
          return;
        }
        setLine(id);
      })();

      return () => {
        cancelled = true;
      };
    }

    if ((!enabled || price == null || !Number.isFinite(price)) && line) {
      withChart(widget, (chart) => chart.removeEntity(line));
      setLine(null);
    }
  }, [
    widget,
    price,
    line,
    enabled,
    draggable,
    lineColor,
    lineStyle,
    text,
    fontSize,
    textColor,
    horzLabelsAlign,
  ]);

  // Keep the controlled line in sync with the latest price and appearance.
  useEffect(() => {
    if (!widget || !enabled || price == null || !Number.isFinite(price) || !line) return;
    withChart(widget, (chart) => {
      const shape = chart.getShapeById(line);
      if (!shape) return;
      shape.setPoints([{ time: Math.floor(Date.now() / 1000), price }]);
      shape.setProperties({ text: text ?? undefined, linecolor: lineColor, textcolor: textColor });
    });
  }, [line, price, widget, enabled, text, lineColor, textColor]);

  useEffect(() => {
    if (!widget || !line) return;

    const handler = (sourceId: EntityId, eventType: DrawingEventType) => {
      if (sourceId !== line) return;

      // The user dragged the line — report the new price to the caller.
      if (eventType === 'points_changed') {
        const draggedPrice = withChart(
          widget,
          (chart) => chart.getShapeById(line)?.getPoints()[0]?.price,
        );
        if (typeof draggedPrice === 'number') onPriceChange?.(draggedPrice);
        return;
      }

      // We fully own this line: a `remove` (user deleted it) or a
      // `properties_changed` (user edited its appearance) both get torn down so
      // the create effect recreates our controlled line at the current price.
      // On `remove` the shape is already gone, so only delete it for an edit.
      if (eventType === 'remove' || eventType === 'properties_changed') {
        if (eventType === 'properties_changed') {
          withChart(widget, (chart) => chart.removeEntity(line));
        }
        setLine(null);
      }
    };

    try {
      widget.subscribe('drawing_event', handler);
    } catch {
      return;
    }
    return () => {
      try {
        widget.unsubscribe('drawing_event', handler);
      } catch {
        // The widget may have been torn down; ignore.
      }
    };
  }, [widget, line, onPriceChange]);
};
